import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FeedbackType } from '../../../NPOFeedback/SubmitReady/utils';
import {
  CustomQuestion,
  CustomQuestionAnswer,
} from 'admin/pages/ManageAccounts/CustomQuestions/utilts';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import ReportCustomQuestionType from './ReportCustomQuestionType';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactProgress from 'shared/design-system/components/TrueImpactProgress';
import { COLORS } from 'shared/design-system/settings/variables/Colors';

export const updateCustomQuestionAnswer = (
  customQuestionIndex: number,
  questionIndex: number,
  questionId: number | string,
  answer: CustomQuestionAnswer,
) => (s: CustomQuestion[]) => {
  const newState = [...s];
  const customQuestion = newState[customQuestionIndex];
  const question = customQuestion.questions[questionIndex];
  const a = {
    dbId: answer.dbId,
    optionId: answer.optionId,
    value: answer.value,
  };
  if (question.answer) {
    question.answer = [...question.answer];
    question.answer[0] = a;
  } else {
    question.answer = [a];
  }
  return newState;
};

const isValidCustomQuestion = (
  c: CustomQuestion,
  isFinalizeStage: boolean,
): boolean => {
  if (!c.reportStage) return false;
  if (c.reportStage.includes('All Stages')) return true;
  if (isFinalizeStage && !c.reportStage.includes('FINAL')) return false;
  if (!isFinalizeStage && c.reportStage.includes('FINAL')) return false;
  return true;
};

export const hasSavedAnswer = (q: CustomQuestion['questions'][number]) =>
  Boolean(q.answer && q.answer[0] && q.answer[0].value);

const getAnsweredQuestionIds = (
  customQuestions: CustomQuestion[],
  isFinalizeStage: boolean,
) => {
  const ids = new Set<string | number>();
  customQuestions.forEach(c => {
    if (!isValidCustomQuestion(c, isFinalizeStage)) return;
    c.questions.forEach(q => {
      if (hasSavedAnswer(q)) ids.add(q.dbId);
    });
  });
  return ids;
};

const getTotalQuestions = (
  customQuestions: CustomQuestion[],
  isFinalizeStage: boolean,
) => {
  return customQuestions.reduce(
    (acc, c) =>
      isValidCustomQuestion(c, isFinalizeStage)
        ? acc + c.questions.length
        : acc,
    0,
  );
};

interface ReportCustomQuestionsProps {
  reportId: number | string;
  isReportPassed: boolean;
  isFinalizeStage: boolean;
  programName: string;
  feedbackType: FeedbackType;
  customQuestions: CustomQuestion[];
  handleHideSurvey: () => unknown;
  setCustomQuestions: React.Dispatch<React.SetStateAction<CustomQuestion[]>>;
}

const StyledDiv = styled.div`
  padding: ${SPACERS.xExtraLarge} ${SPACERS.large} ${SPACERS.large};
  .survey-header {
    max-width: 750px;
    margin: 0 auto ${SPACERS.medium};
    text-align: center;
    .survey-subtitle {
      margin-top: ${SPACERS.tiny};
      color: ${COLORS.grayDark};
    }
  }
  .survey-progress-card {
    max-width: 750px;
    margin: 0 auto ${SPACERS.medium};
    padding: ${SPACERS.small} ${SPACERS.medium};
    border-radius: ${SPACERS.tiny};
    background-color: ${COLORS.whiteBase};
    border: 1px solid ${COLORS.grayLight};
    .survey-progress-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: ${SPACERS.small};
      margin-bottom: ${SPACERS.tiny};
    }
    .survey-progress-count {
      color: ${COLORS.grayDark};
    }
    .progress-background,
    .progress-foreground {
      height: 6px;
    }
  }
  .survey-footer {
    position: sticky;
    bottom: 0;
    max-width: 750px;
    margin: ${SPACERS.medium} auto 0;
    padding: ${SPACERS.small} ${SPACERS.medium};
    border-radius: ${SPACERS.tiny};
    background-color: ${COLORS.whiteBase};
    border: 1px solid ${COLORS.grayLight};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${SPACERS.small};
    .survey-footer-hint {
      color: ${COLORS.grayDark};
    }
    .done-button {
      min-width: 160px;
    }
  }
`;

const ReportCustomQuestions: React.FC<ReportCustomQuestionsProps> = ({
  reportId,
  isReportPassed,
  isFinalizeStage,
  programName,
  feedbackType,
  customQuestions,
  handleHideSurvey,
  setCustomQuestions,
}) => {
  const totalQuestions = useMemo(
    () => getTotalQuestions(customQuestions, isFinalizeStage),
    [customQuestions],
  );
  const [doneQuestionIds, setDoneQuestionIds] = useState<Set<string | number>>(
    () => getAnsweredQuestionIds(customQuestions, isFinalizeStage),
  );

  const handleAddDoneQuestionId = useCallback(
    (questionId: number | string) => {
      setDoneQuestionIds(s => {
        const newState = new Set(s);
        newState.add(questionId);
        return newState;
      });
    },
    [setDoneQuestionIds],
  );
  const handleRemoveDoneQuestionId = useCallback(
    (questionId: number | string) => {
      setDoneQuestionIds(s => {
        const newState = new Set(s);
        newState.delete(questionId);
        return newState;
      });
    },
    [setDoneQuestionIds],
  );
  const handleChange = useCallback(
    (
      customQuestionIndex: number,
      questionIndex: number,
      questionId: number | string,
      answer: CustomQuestionAnswer,
    ) => {
      setCustomQuestions(
        updateCustomQuestionAnswer(
          customQuestionIndex,
          questionIndex,
          questionId,
          answer,
        ),
      );
      handleAddDoneQuestionId(questionId);
    },
    [handleAddDoneQuestionId],
  );

  const doneCount = Math.min(doneQuestionIds.size, totalQuestions);
  const isAllDone = doneCount === totalQuestions;
  const remaining = totalQuestions - doneCount;

  return (
    <StyledDiv>
      <div className="survey-header">
        <TrueImpactText variant="heading">Report Survey</TrueImpactText>
        <TrueImpactText className="survey-subtitle">
          A few additional questions about this report.
          <br />
          Save each answer, then mark the survey as done to return to your
          review.
        </TrueImpactText>
      </div>
      <div className="survey-progress-card">
        <div className="survey-progress-row">
          <TrueImpactText variant="body-bold">
            {isAllDone
              ? 'All answers saved'
              : `${remaining} question${remaining === 1 ? '' : 's'} remaining`}
          </TrueImpactText>
          <TrueImpactText
            variant="body-small"
            className="survey-progress-count"
          >
            {doneCount} of {totalQuestions} saved
          </TrueImpactText>
        </div>
        <TrueImpactProgress
          total={totalQuestions}
          completed={doneCount}
          color="grayDarkest"
        />
      </div>
      {customQuestions.map((c, i) => {
        if (!isValidCustomQuestion(c, isFinalizeStage)) return null;
        return c.questions.map((q, j) => (
          <ReportCustomQuestionType
            key={q.dbId}
            reportId={reportId}
            answer={q.answer}
            initialEdit={!hasSavedAnswer(q)}
            onChange={handleChange}
            customQuestionIndex={i}
            customQuestion={c}
            questionIndex={j}
            question={q}
            handleAddDoneQuestionId={handleAddDoneQuestionId}
            handleRemoveDoneQuestionId={handleRemoveDoneQuestionId}
          />
        ));
      })}
      <div className="survey-footer">
        <TrueImpactText variant="body-small" className="survey-footer-hint">
          {isAllDone
            ? 'All answers saved.'
            : `${remaining} question${
                remaining === 1 ? '' : 's'
              } left to save before you can continue.`}
        </TrueImpactText>
        <TrueImpactButton
          className="done-button"
          disabled={!isAllDone}
          onClick={handleHideSurvey}
        >
          Done
        </TrueImpactButton>
      </div>
    </StyledDiv>
  );
};

export default ReportCustomQuestions;
